/**
 * BrandLogo — the single source of truth for the Aisistoo v37 logo (BRAND-1/BRAND-2).
 *
 * Renders the pre-coloured v37 lockups from /public/brand/ as ONE <img>. Uses the
 * hi-res PNG exports (logo-*.png, ~1900px wide) — BRAND-3 retired the hand-authored
 * SVG lockups because their mark was mis-scaled/misaligned (thin broken strokes).
 * NEVER rebuild the lockup from icon + text parts, and never
 * recolour/stretch — pick the matching tone file and a canonical `size` preset:
 *
 *   tone  "purple" (light surfaces) · "white" (dark/purple) · "ink" (low-emphasis)
 *
 * CANONICAL SIZE SYSTEM (BRAND-2 — use these presets everywhere; the raw `height`
 * prop is a legacy escape hatch only for one-off auth marks with no matching preset):
 *   login             → horizontal, width clamp(220px, 26vw, 320px)   (login brand panel)
 *   sidebar           → horizontal, height 36px                        (sidebar expanded)
 *   sidebar-collapsed → icon, 36×36                                    (sidebar collapsed)
 *   mobile            → horizontal, height 28px                        (mobile top bar)
 *   empty             → icon, 48×48, 40% opacity                       (empty/error states)
 *
 * IMPORTANT (no-stretch rule): to keep the lockup's aspect ratio, NEVER put
 * `flex: 1` directly on this <img> — a flex-grown replaced element distorts.
 * Wrap it in a `flex: 1` container instead and let the img keep intrinsic width.
 *
 * PROD-POLISH-2 A1 — that rule was followed and the logo still distorted, because there is a
 * SECOND way to stretch a replaced element: a COLUMN flex parent's cross axis is horizontal, so
 * the default `align-items: stretch` blows a `width: auto` img out to the parent's full width.
 * Four mobile headers hit this (student/admin/employee/teacher): measured 322px wide at 28px tall
 * on a 390px viewport — a 14.5× stretch that reads as a corrupted purple scribble. The call sites
 * now pass `alignSelf: 'flex-start'`, and `maxWidth: '100%'` below stops the mirror-image failure
 * (a lockup wider than a narrow parent overflowing it). Keep BOTH.
 *
 * @module components/brand/BrandLogo
 */
import type { CSSProperties } from 'react';

type Variant = 'horizontal' | 'icon' | 'stacked';
type Tone = 'purple' | 'white' | 'ink';
type Size = 'login' | 'sidebar' | 'sidebar-collapsed' | 'mobile' | 'empty';

const SIZE_PRESETS: Record<Size, { variant: Variant; style: CSSProperties }> = {
  login: { variant: 'horizontal', style: { width: 'clamp(220px, 26vw, 300px)', height: 'auto' } },
  sidebar: { variant: 'horizontal', style: { height: 40, width: 'auto' } },
  'sidebar-collapsed': { variant: 'icon', style: { height: 36, width: 'auto' } },
  mobile: { variant: 'icon', style: { height: 28, width: 'auto' } },
  empty: { variant: 'icon', style: { height: 48, width: 'auto', opacity: 0.4 } },
};

interface BrandLogoProps {
  /** Ignored when `size` is set (the preset owns the variant). */
  variant?: Variant;
  tone?: Tone;
  /** Canonical size preset — prefer this over `height`. */
  size?: Size;
  /** Legacy escape hatch (px height) for one-off marks with no matching preset. */
  height?: number;
  alt?: string;
  style?: CSSProperties;
  className?: string;
}

export function BrandLogo({
  variant,
  tone = 'purple',
  size,
  height,
  alt = 'Aisistoo',
  style,
  className,
}: BrandLogoProps) {
  const preset = size ? SIZE_PRESETS[size] : undefined;
  const resolvedVariant = preset?.variant ?? variant ?? 'horizontal';
  const baseStyle: CSSProperties = preset
    ? preset.style
    : { height: height ?? (resolvedVariant === 'icon' ? 32 : 28), width: 'auto' };
  return (
    <img
      src={`/brand/logo-${resolvedVariant}-${tone}.png`}
      alt={alt}
      draggable={false}
      className={className}
      style={{ display: 'block', flexShrink: 0, maxWidth: '100%', objectFit: 'contain', ...baseStyle, ...style }}
    />
  );
}

export default BrandLogo;
